import { useEffect, useState } from "react";
import { AlertTriangle, Clock } from "lucide-react";
import { ConfirmDialog } from "../../../../../components/ui/confirm-dialog";
import { Switch } from "../../../../../components/ui/switch";
import { Label } from "../../../../../components/ui/label";

/**
 * EnviarOfertasDialog — confirmação do ENVIO ATIVO de ofertas (split entrego).
 *
 * Não usa o OperationConfirmDialog porque o envio exige dupla confirmação:
 * um toggle de armamento + o botão Confirmar (que só habilita com o toggle ligado).
 * O backend filtra franquias do piloto e remove quem já aceitou a oferta.
 *
 * TEMP-SPLIT-OFERTA — PILOTO ENCERRADO (2026-10-02): componente do piloto de split
 * da Rayo. Sem uso hoje (checkout-action-buttons só o renderiza quando recebe
 * onEnviarOfertas, que nenhuma página passa mais). Mantido para eventual retomada.
 */

interface EnviarOfertasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  ops: string;
  date: string;
  loading?: boolean;
}

// TEMP-SPLIT-OFERTA: regra de horário do piloto — envio preferencial após as
// 13:00 (GMT-3). Só alerta; não bloqueia.
const HORA_PREFERENCIAL_GMT3 = 13;

function isAntesDoHorarioPreferencial(): boolean {
  const horaGmt3 = Number(
    new Intl.DateTimeFormat("pt-BR", {
      hour: "numeric",
      hour12: false,
      timeZone: "America/Sao_Paulo",
    }).format(new Date())
  );
  return horaGmt3 < HORA_PREFERENCIAL_GMT3;
}

export function EnviarOfertasDialog({
  open,
  onOpenChange,
  onConfirm,
  ops,
  date,
  loading = false,
}: EnviarOfertasDialogProps) {
  const [armado, setArmado] = useState(false);
  const antesDoHorario = open ? isAntesDoHorarioPreferencial() : false;

  // Toggle sempre desarmado ao abrir/fechar — cada envio exige rearmar.
  useEffect(() => {
    if (!open) setArmado(false);
  }, [open]);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      title="Enviar ofertas?"
      confirmText={loading ? "Processando..." : "Confirmar envio"}
      cancelText="Cancelar"
      confirmDisabled={!armado || loading}
    >
      <div className="space-y-4">
        <div>
          <p className="font-medium text-foreground">
            Envio ativo de ofertas via WhatsApp
          </p>
          <p className="text-sm text-muted-foreground">
            OPS: {ops.toUpperCase()}, Data: {date}
          </p>
          <p className="text-sm text-muted-foreground">
            A oferta será enviada somente para quem ainda não a aceitou.
          </p>
        </div>

        <div
          className={
            antesDoHorario
              ? "flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-400"
              : "flex items-start gap-2 rounded-md border p-3 text-sm text-muted-foreground"
          }
        >
          {antesDoHorario ? (
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          ) : (
            <Clock className="h-4 w-4 mt-0.5 shrink-0" />
          )}
          <span>
            O envio deve ser feito preferencialmente depois das 13:00 (GMT-3).
            {antesDoHorario && " Ainda não são 13:00 — confirme se deseja enviar mesmo assim."}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id="armar-envio-ofertas"
            checked={armado}
            onCheckedChange={setArmado}
            disabled={loading}
          />
          <Label htmlFor="armar-envio-ofertas" className="cursor-pointer">
            Confirmo o envio ativo das ofertas
          </Label>
        </div>
      </div>
    </ConfirmDialog>
  );
}
